/**
 * CalculatorStudio.tsx — the Studio shell for ONE calculator. Loads its CalcSpec
 * (+ lookup tables) via GET /configs/:category/spec, exposes every editor
 * (inputs, formulas, lookups, recommendation, presentation, proposal section,
 * test-run), and Save (PUT spec) + Revert (single-level).
 *
 * Works identically for legacy (engine-ported, configBacked) and brand-new
 * calculators — the spec is the single source of truth. Prices are never edited
 * here (read-only from Product.unitPrice).
 */

import { useCallback, useEffect, useState } from 'react';
import { Save, RotateCcw, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  calculatorBuilderApi,
  type CalcSpec,
  type LookupTable,
  type SpecFormula,
  type SpecRecommendation,
  type StudioInputField,
} from '../../../api/calculatorBuilder';
import InputsDesigner from './InputsDesigner';
import FormulaEditor from './FormulaEditor';
import LookupTableEditor from './LookupTableEditor';
import RecommendationEditor from './RecommendationEditor';
import PresentationEditor from './PresentationEditor';
import ProposalSectionEditor from './ProposalSectionEditor';
import TestRunPanel from './TestRunPanel';
import { btn, btnAccent } from './ui';

type Tab = 'inputs' | 'formulas' | 'lookups' | 'recommend' | 'presentation' | 'proposal' | 'test';

const TABS: { id: Tab; label: string }[] = [
  { id: 'inputs', label: 'Inputs' },
  { id: 'formulas', label: 'Formulas' },
  { id: 'lookups', label: 'Lookup tables' },
  { id: 'recommend', label: 'Recommendation' },
  { id: 'presentation', label: 'Presentation' },
  { id: 'proposal', label: 'Proposal section' },
  { id: 'test', label: 'Test run' },
];

const EMPTY_SPEC: CalcSpec = {
  formulas: [],
  lookupRefs: [],
  recommendation: { productKey: '', filters: [], criteria: [], slots: [] },
  presentation: { html: '' },
  proposalSection: { html: '' },
  inputs: [],
};

interface Props {
  category: string;
  /** optional proposal CSS to render inside the HTML editors (matches proposal look). */
  proposalCss?: string;
  onReverted?: () => void;
}

export default function CalculatorStudio({ category, proposalCss, onReverted }: Props) {
  const [tab, setTab] = useState<Tab>('inputs');
  const [spec, setSpec] = useState<CalcSpec>(EMPTY_SPEC);
  const [inputs, setInputs] = useState<StudioInputField[]>([]);
  const [tables, setTables] = useState<LookupTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      const res = await calculatorBuilderApi.getSpec(category);
      const loaded = { ...EMPTY_SPEC, ...res.spec };
      setSpec(loaded);
      setInputs((loaded.inputs as StudioInputField[]) ?? []);
      setTables(res.lookupTables ?? []);
      setDirty(false);
    } catch (e: any) {
      setStatus({ kind: 'err', msg: e?.response?.data?.message || 'Could not load this calculator.' });
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    void load();
  }, [load]);

  const mut = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
    setStatus(null);
  };

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      // inputs are carried on the spec so they persist alongside it.
      const payload: CalcSpec = { ...spec, inputs };
      await calculatorBuilderApi.putSpec(category, payload);
      setDirty(false);
      setStatus({ kind: 'ok', msg: 'Saved. Live immediately.' });
    } catch (e: any) {
      setStatus({ kind: 'err', msg: e?.response?.data?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    if (!window.confirm('Revert to the last saved snapshot? This restores the spec + lookup tables.')) return;
    setSaving(true);
    try {
      await calculatorBuilderApi.revertCategory(category);
      await load();
      setStatus({ kind: 'ok', msg: 'Reverted to the previous snapshot.' });
      onReverted?.();
    } catch (e: any) {
      setStatus({ kind: 'err', msg: e?.response?.data?.message || 'Revert failed (no snapshot yet?).' });
    } finally {
      setSaving(false);
    }
  };

  // Lookup-table save/delete go through their own endpoints (persist immediately).
  const saveTable = async (t: LookupTable) => {
    const exists = (await calculatorBuilderApi.getSpec(category)).lookupTables.some((x) => x.tableKey === t.tableKey);
    if (exists) {
      await calculatorBuilderApi.putLookupTable(category, t.tableKey, { columns: t.columns, rows: t.rows, mode: t.mode });
    } else {
      await calculatorBuilderApi.addLookupTable(category, t);
    }
    setStatus({ kind: 'ok', msg: `Lookup table “${t.tableKey}” saved.` });
  };
  const deleteTable = async (tableKey: string) => {
    if (!window.confirm(`Delete lookup table “${tableKey}”?`)) return;
    await calculatorBuilderApi.deleteLookupTable(category, tableKey);
    setTables((prev) => prev.filter((t) => t.tableKey !== tableKey));
    setStatus({ kind: 'ok', msg: `Lookup table “${tableKey}” deleted.` });
  };

  if (loading) {
    return (
      <div style={{ padding: 40, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink-3, #6b7280)' }}>
        <Loader2 size={16} className="spin" /> Loading calculator…
      </div>
    );
  }

  return (
    <div>
      {/* header: tabs + save/revert */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 14,
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: 'var(--bg-0, #f9fafb)',
          padding: '4px 0',
        }}
      >
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                ...btn,
                background: tab === t.id ? 'var(--accent, #4f46e5)' : 'var(--card, #fff)',
                color: tab === t.id ? '#fff' : 'var(--ink-2, #374151)',
                border: `1px solid ${tab === t.id ? 'var(--accent, #4f46e5)' : 'var(--line, #e5e7eb)'}`,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {status && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12.5,
                color: status.kind === 'ok' ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)',
              }}
            >
              {status.kind === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
              {status.msg}
            </span>
          )}
          <button type="button" style={btn} onClick={revert} disabled={saving}>
            <RotateCcw size={14} /> Revert
          </button>
          <button type="button" style={btnAccent} onClick={save} disabled={saving || !dirty}>
            {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} {dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>

      {tab === 'inputs' && <InputsDesigner inputs={inputs} onChange={mut((v: StudioInputField[]) => setInputs(v))} />}
      {tab === 'formulas' && (
        <FormulaEditor
          spec={{ ...spec, inputs }}
          inputs={inputs}
          onChange={mut((f: SpecFormula[]) => setSpec((s) => ({ ...s, formulas: f })))}
        />
      )}
      {tab === 'lookups' && (
        <LookupTableEditor tables={tables} onChange={(v) => setTables(v)} onSaveTable={saveTable} onDeleteTable={deleteTable} />
      )}
      {tab === 'recommend' && (
        <RecommendationEditor
          recommendation={spec.recommendation}
          onChange={mut((r: SpecRecommendation) => setSpec((s) => ({ ...s, recommendation: r })))}
        />
      )}
      {tab === 'presentation' && (
        <PresentationEditor
          spec={{ ...spec, inputs }}
          inputs={inputs}
          css={proposalCss}
          onChange={mut((html: string) => setSpec((s) => ({ ...s, presentation: { html } })))}
        />
      )}
      {tab === 'proposal' && (
        <ProposalSectionEditor
          spec={{ ...spec, inputs }}
          inputs={inputs}
          css={proposalCss}
          onChange={mut((html: string) => setSpec((s) => ({ ...s, proposalSection: { html } })))}
        />
      )}
      {tab === 'test' && <TestRunPanel category={category} inputs={inputs} />}
    </div>
  );
}
